import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Grid,
  Slide,
  Link,
  makeStyles,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import * as api from "../forecastApi";
import ForecastHistoryList from "./ForecastHistoryList";
import ForecastHistoryTable from "./ForecastHistoryTable";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="left" ref={ref} {...props} />
));

const useStyles = makeStyles((theme) => ({
  paper: { backgroundColor: "#fff" },
  header: {
    padding: theme.spacing(2, 3, 1, 3),
    borderBottom: "1px solid #e0e0e0",
    display: "flex",
    gap: "10px"
  },
  back: {
    display: "inline-flex",
    alignItems: "center",
    color: "#212121",
    textDecoration: "none",
    fontSize: "0.85rem",
    cursor: "pointer",
    marginBottom: theme.spacing(1),
    "&:hover": { color: "#1976d2" },
  },
  title: { fontWeight: 700, fontSize: "1.4rem" },
  body: { height: "calc(100vh - 110px)" },
  leftPane: {
    borderRight: "1px solid #e0e0e0",
    height: "100%",
    overflowY: "auto",
  },
  rightPane: { height: "100%", overflowY: "auto" },
}));

export default function ForecastHistoryDialog({ open, filters, onClose }) {
  const classes = useStyles();

  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);

  const [historyDetailsLoading, setHistoryDetailsLoading] = useState(false);
  const [historyDetails, setHistoryDetails] = useState([]);

  // Load history when dialog opens
  useEffect(() => {
    if (!open) {
      setHistory([]);
      setSelectedHistoryId(null);
      setHistoryDetails([]);
      return undefined;
    }
    const ctrl = new AbortController();
    setHistoryLoading(true);
    const payload = {
      cmoOrgID: filters.cmoOrgID,
      sponsorOrgID: filters.sponsorOrgID,
      brand: filters.brand,
      aslNumber: filters.aslNumber || null,
      startMonth: filters.startMonth,
      endMonth: filters.endMonth,
    };
    api
      .fetchForecastHistory(payload, ctrl.signal)
      .then((res) => {
        const list =
          (res && res.status && res.data && res.data.forecastDataList) || [];
        const sorted = [...list].sort(
          (a, b) =>
            new Date(b.forecastSyncDate).getTime() -
            new Date(a.forecastSyncDate).getTime()
        );
        setHistory(sorted);
        setSelectedHistoryId(
          sorted.length > 0 ? sorted[0].collabrixForecastSyncID : null
        );
      })
      .catch(() => {
        setHistory([]);
        setSelectedHistoryId(null);
      })
      .finally(() => setHistoryLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load details whenever a history version is selected
  useEffect(() => {
    if (!open || !selectedHistoryId) return undefined;
    const ctrl = new AbortController();
    setHistoryDetailsLoading(true);
    const payload = {
      cmoOrgID: filters.cmoOrgID,
      sponsorOrgID: filters.sponsorOrgID,
      collabrixForecastSyncID: selectedHistoryId,
      brand: filters.brand,
      aslNumber: filters.aslNumber || null,
      startMonth: filters.startMonth,
      endMonth: filters.endMonth,
      pageNumber: 0,
      pageSize: 0,
    };
    api
      .fetchForecasts(payload, ctrl.signal)
      .then((res) => {
        setHistoryDetails(
          (res && res.status && res.data && res.data.forecastDetailsList) || []
        );
      })
      .catch(() => setHistoryDetails([]))
      .finally(() => setHistoryDetailsLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHistoryId, open]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      classes={{ paper: classes.paper }}
    >
      <Box className={classes.header}>
        <Link className={classes.back} onClick={onClose}>
          <ChevronLeftIcon fontSize="small" />
          Back
        </Link>
        <Typography className={classes.title}>
          View forecast history - {localStorage.collabType !== 'CMO' ? `CMO ${filters.cmoLabel || "-"}`: `Sponsor ${filters.sponsorLabel || "-"}`}, Brand{" "}
          {filters.brand || "-"}
        </Typography>
      </Box>
      <Box className={classes.body}>
        <Grid container style={{ height: "100%" }}>
          <Grid item xs={12} md={2} className={classes.leftPane}>
            <ForecastHistoryList
              history={history}
              loading={historyLoading}
              selectedHistoryId={selectedHistoryId}
              onSelect={setSelectedHistoryId}
              filters={filters}
            />
          </Grid>
          <Grid item xs={12} md={10} className={classes.rightPane}>
            <ForecastHistoryTable
              historyDetails={historyDetails}
              historyDetailsLoading={historyDetailsLoading}
              selectedHistoryId={selectedHistoryId}
              filters={filters}
            />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}
